import React, { useState } from "react";
import styled from "styled-components";

interface CopyButtonProps {
  value: string;
  ariaLabel?: string;
}

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  padding: 2px 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  font-size: 11px;
  line-height: 1.4;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gray300};
  }

  &:disabled {
    cursor: default;
    color: ${({ theme }) => theme.colors.green};
    border-color: ${({ theme }) => theme.colors.green};
  }
`;

const copyToClipboard = async (value: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through to legacy path
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

const CopyButton: React.FC<CopyButtonProps> = ({ value, ariaLabel }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyToClipboard(value);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Button
      type="button"
      onClick={handleCopy}
      disabled={copied}
      aria-label={ariaLabel || `Copy ${value}`}
    >
      {copied ? "Copied" : "Copy"}
    </Button>
  );
};

export default CopyButton;
